/**
 * OnboardingPage — SaaS Workspace Onboarding Wizard
 *
 * 4-step flow: Workspace → Plan → Organization → Done
 * Route: /onboarding (requires auth, no workspace required)
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import {
  createWorkspace,
  generateSlug,
  isSlugAvailable,
  getPlanBySlug,
} from '../../../services/workspaceService';
import { supabase } from '../../../lib/supabaseClient';
import type { OnboardingData, PlanSlug, BillingCycle } from '../../../types/saasTypes';
import {
  Building2,
  Rocket,
  CreditCard,
  Users,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Mail,
  Plus,
  Trash2,
} from 'lucide-react';

// ─── Step definitions ─────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Workspace',     icon: Building2 },
  { id: 2, label: 'Plano',         icon: CreditCard },
  { id: 3, label: 'Organização',   icon: Users },
  { id: 4, label: 'Equipe',        icon: Users },
  { id: 5, label: 'Pronto!',       icon: Rocket },
];

// ─── Plan cards ───────────────────────────────────────────────

interface PlanOption {
  slug: PlanSlug;
  name: string;
  price: number;
  priceYear: number;
  description: string;
  features: string[];
  highlighted?: boolean;
}

const PLANS: PlanOption[] = [
  {
    slug: 'free',
    name: 'Free',
    price: 0,
    priceYear: 0,
    description: 'Para começar a explorar',
    features: ['Até 3 usuários', '5 processos', '2 projetos', 'Kanban + VSM'],
  },
  {
    slug: 'starter',
    name: 'Starter',
    price: 97,
    priceYear: 970,
    description: 'Para times em crescimento',
    features: ['Até 10 usuários', '25 processos', '10 projetos', 'Modelos + exportação PDF', 'Monitor de Ganhos'],
    highlighted: true,
  },
  {
    slug: 'pro',
    name: 'Pro',
    price: 297,
    priceYear: 2970,
    description: 'Para operações maduras',
    features: ['Até 50 usuários', '100 processos', '50 projetos', 'IA + Analytics avançados', 'SSO + API + Webhooks'],
  },
  {
    slug: 'enterprise',
    name: 'Enterprise',
    price: 0,
    priceYear: 0,
    description: 'Para grandes organizações',
    features: ['Usuários ilimitados', 'Processos ilimitados', 'SLA garantido', 'Suporte dedicado'],
  },
];

// ─── Main component ───────────────────────────────────────────

const INITIAL_DATA: OnboardingData = {
  workspaceName: '',
  workspaceSlug: '',
  selectedPlanSlug: 'starter',
  billingCycle: 'monthly',
  organizationName: '',
  industry: '',
  teamSize: '',
  inviteEmails: [],
};

const STORAGE_KEY = 'wf_onboarding_progress';

function loadSavedProgress(): { step: number; data: OnboardingData } | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveProgress(step: number, data: OnboardingData) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ step, data }));
  } catch { /* ignore */ }
}

function clearProgress() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const saved = loadSavedProgress();
  const [step, setStep] = useState(saved?.step || 1);
  const [data, setData] = useState<OnboardingData>(saved?.data || INITIAL_DATA);
  const [slugError, setSlugError] = useState('');
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);

  // Persist progress on changes
  useEffect(() => {
    if (step < 5) saveProgress(step, data);
  }, [step, data]);

  const patch = useCallback((fields: Partial<OnboardingData>) => {
    setData((prev) => ({ ...prev, ...fields }));
  }, []);

  // Auto-generate slug from workspace name
  const handleWorkspaceNameChange = useCallback(async (name: string) => {
    const slug = generateSlug(name);
    patch({ workspaceName: name, workspaceSlug: slug });
    if (slug.length >= 3) {
      setIsCheckingSlug(true);
      setSlugError('');
      const available = await isSlugAvailable(slug);
      setIsCheckingSlug(false);
      if (!available) setSlugError('Este slug já está em uso. Edite manualmente.');
    }
  }, [patch]);

  const handleSlugChange = useCallback(async (raw: string) => {
    const slug = generateSlug(raw);
    patch({ workspaceSlug: slug });
    if (slug.length >= 3) {
      setIsCheckingSlug(true);
      setSlugError('');
      const available = await isSlugAvailable(slug);
      setIsCheckingSlug(false);
      if (!available) setSlugError('Este slug já está em uso.');
    }
  }, [patch]);

  // Final submission
  const completeMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('Usuário não autenticado');

      // 1. Get selected plan ID
      const plan = await getPlanBySlug(data.selectedPlanSlug);

      // 2. Create workspace
      const workspace = await createWorkspace({
        name: data.workspaceName,
        slug: data.workspaceSlug,
        ownerUserId: user.id,
        planId: plan?.id,
      });

      // 3. Create organization linked to workspace
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: data.organizationName || data.workspaceName,
          workspace_id: workspace.id,
        })
        .select('id')
        .single();

      if (orgError) throw new Error(orgError.message);

      // 4. Add user to org
      await supabase
        .from('user_organizations')
        .insert({
          user_id: user.id,
          organization_id: org.id,
          role: 'admin',
        })
        .select()
        .maybeSingle();

      // 5. Send team invites (non-blocking)
      const validEmails = data.inviteEmails.filter(e => e.includes('@'));
      if (validEmails.length > 0) {
        const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
        await supabase.from('user_invitations').insert(
          validEmails.map(email => ({
            organization_id: org.id,
            email,
            role: 'operator',
            invited_by: user.id,
            token: crypto.randomUUID(),
            status: 'pending',
            expires_at: expiresAt,
          }))
        ).then(() => {}, () => {}); // fire-and-forget
      }

      return workspace;
    },
    onSuccess: () => {
      clearProgress();
      setStep(5);
    },
  });

  const canAdvanceStep1 =
    data.workspaceName.length >= 2 &&
    data.workspaceSlug.length >= 3 &&
    !slugError &&
    !isCheckingSlug;

  const canAdvanceStep3 =
    data.organizationName.length >= 2 && data.industry && data.teamSize;

  const handleNext = () => {
    if (step === 4) {
      completeMutation.mutate();
    } else if (step < 5) {
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep((s) => s - 1);
  };

  return (
    <div className="onboarding-shell">
      {/* Progress bar */}
      <header className="onboarding-header">
        <div className="onboarding-logo">Workflow Manager</div>
        <div className="onboarding-steps">
          {STEPS.map((s) => {
            const Icon = s.icon;
            const done = step > s.id;
            const active = step === s.id;
            return (
              <div key={s.id} className={`onboarding-step-item ${active ? 'active' : done ? 'done' : ''}`}>
                <div className="onboarding-step-circle">
                  {done ? <CheckCircle2 size={14} /> : <Icon size={14} />}
                </div>
                <span>{s.label}</span>
              </div>
            );
          })}
        </div>
      </header>

      {/* Card */}
      <main className="onboarding-main">
        <div className="onboarding-card">
          {step === 1 && (
            <StepWorkspace
              data={data}
              slugError={slugError}
              isCheckingSlug={isCheckingSlug}
              onNameChange={handleWorkspaceNameChange}
              onSlugChange={handleSlugChange}
            />
          )}
          {step === 2 && (
            <StepPlan data={data} onChange={patch} />
          )}
          {step === 3 && (
            <StepOrganization data={data} onChange={patch} />
          )}
          {step === 4 && (
            <StepTeamInvite data={data} onChange={patch} />
          )}
          {step === 5 && (
            <StepDone workspaceName={data.workspaceName} inviteCount={data.inviteEmails.filter(e => e.includes('@')).length} />
          )}

          {/* Error banner */}
          {completeMutation.isError && (
            <div className="onboarding-error">
              <AlertCircle size={16} />
              <span>{(completeMutation.error as Error).message}</span>
            </div>
          )}

          {/* Navigation buttons */}
          {step < 5 && (
            <div className="onboarding-nav">
              {step > 1 && (
                <button className="onboarding-btn-secondary" onClick={handleBack} disabled={completeMutation.isPending}>
                  <ArrowLeft size={16} /> Voltar
                </button>
              )}
              <button
                className="onboarding-btn-primary"
                onClick={handleNext}
                disabled={
                  (step === 1 && !canAdvanceStep1) ||
                  (step === 3 && !canAdvanceStep3) ||
                  completeMutation.isPending
                }
              >
                {completeMutation.isPending ? (
                  <><Loader2 size={16} className="spin" /> Criando...</>
                ) : step === 4 ? (
                  <><Rocket size={16} /> Criar Workspace</>
                ) : (
                  <>Continuar <ArrowRight size={16} /></>
                )}
              </button>
            </div>
          )}

          {step === 5 && (
            <div className="onboarding-nav" style={{ justifyContent: 'center' }}>
              <button className="onboarding-btn-primary" onClick={() => navigate('/centro-operacional')}>
                <Rocket size={16} /> Acessar Plataforma
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// ─── Step 1: Workspace ────────────────────────────────────────

function StepWorkspace({
  data,
  slugError,
  isCheckingSlug,
  onNameChange,
  onSlugChange,
}: {
  data: OnboardingData;
  slugError: string;
  isCheckingSlug: boolean;
  onNameChange: (v: string) => void;
  onSlugChange: (v: string) => void;
}) {
  return (
    <div className="onboarding-step-body">
      <div className="onboarding-step-icon"><Building2 size={28} /></div>
      <h2>Crie seu Workspace</h2>
      <p className="onboarding-step-desc">
        O workspace é o ambiente central da sua equipe. Você pode ter múltiplos workspaces.
      </p>
      <div className="onboarding-field">
        <label>Nome do Workspace</label>
        <input
          type="text"
          value={data.workspaceName}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Ex: Acme Corp, Minha Empresa..."
          autoFocus
        />
      </div>
      <div className="onboarding-field">
        <label>Slug (identificador único)</label>
        <div className="onboarding-slug-wrapper">
          <span className="onboarding-slug-prefix">app.workflow.com/</span>
          <input
            type="text"
            value={data.workspaceSlug}
            onChange={(e) => onSlugChange(e.target.value)}
            placeholder="acme-corp"
          />
          {isCheckingSlug && <Loader2 size={14} className="spin onboarding-slug-check" />}
        </div>
        {slugError && <span className="onboarding-field-error"><AlertCircle size={12} />{slugError}</span>}
        {!slugError && data.workspaceSlug.length >= 3 && !isCheckingSlug && (
          <span className="onboarding-field-ok"><CheckCircle2 size={12} /> Disponível</span>
        )}
      </div>
    </div>
  );
}

// ─── Step 2: Plan Selection ───────────────────────────────────

function StepPlan({
  data,
  onChange,
}: {
  data: OnboardingData;
  onChange: (fields: Partial<OnboardingData>) => void;
}) {
  const [cycle, setCycle] = useState<BillingCycle>(data.billingCycle);

  const handleCycleChange = (c: BillingCycle) => {
    setCycle(c);
    onChange({ billingCycle: c });
  };

  return (
    <div className="onboarding-step-body">
      <div className="onboarding-step-icon"><CreditCard size={28} /></div>
      <h2>Escolha seu Plano</h2>
      <p className="onboarding-step-desc">
        Comece com 14 dias grátis. Cancele a qualquer momento.
      </p>

      <div className="onboarding-cycle-toggle">
        <button
          className={cycle === 'monthly' ? 'active' : ''}
          onClick={() => handleCycleChange('monthly')}
        >Mensal</button>
        <button
          className={cycle === 'annual' ? 'active' : ''}
          onClick={() => handleCycleChange('annual')}
        >Anual <span className="onboarding-discount-badge">-17%</span></button>
      </div>

      <div className="onboarding-plans-grid">
        {PLANS.map((plan) => (
          <button
            key={plan.slug}
            className={`onboarding-plan-card ${data.selectedPlanSlug === plan.slug ? 'selected' : ''} ${plan.highlighted ? 'highlighted' : ''}`}
            onClick={() => onChange({ selectedPlanSlug: plan.slug })}
          >
            {plan.highlighted && <span className="onboarding-plan-badge">Popular</span>}
            <div className="onboarding-plan-name">{plan.name}</div>
            <div className="onboarding-plan-price">
              {plan.price === 0 && plan.slug !== 'enterprise' ? (
                <span className="price-free">Grátis</span>
              ) : plan.slug === 'enterprise' ? (
                <span className="price-contact">Consultar</span>
              ) : (
                <>
                  <span className="price-currency">R$</span>
                  <span className="price-value">
                    {cycle === 'annual'
                      ? Math.round(plan.priceYear / 12).toLocaleString('pt-BR')
                      : plan.price.toLocaleString('pt-BR')}
                  </span>
                  <span className="price-period">/mês</span>
                </>
              )}
            </div>
            <div className="onboarding-plan-desc">{plan.description}</div>
            <ul className="onboarding-plan-features">
              {plan.features.map((f) => (
                <li key={f}><CheckCircle2 size={12} />{f}</li>
              ))}
            </ul>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Step 3: Organization Details ────────────────────────────

const INDUSTRIES = [
  'Manufatura', 'Logística', 'Saúde', 'Financeiro', 'Tecnologia',
  'Varejo', 'Construção', 'Educação', 'Consultoria', 'Outro',
];

const TEAM_SIZES = ['1–5', '6–20', '21–50', '51–200', '200+'];

function StepOrganization({
  data,
  onChange,
}: {
  data: OnboardingData;
  onChange: (fields: Partial<OnboardingData>) => void;
}) {
  return (
    <div className="onboarding-step-body">
      <div className="onboarding-step-icon"><Users size={28} /></div>
      <h2>Sua Organização</h2>
      <p className="onboarding-step-desc">
        Isso nos ajuda a personalizar sua experiência e relatórios.
      </p>
      <div className="onboarding-field">
        <label>Nome da Organização *</label>
        <input
          type="text"
          value={data.organizationName}
          onChange={(e) => onChange({ organizationName: e.target.value })}
          placeholder="Ex: Acme Indústria Ltda"
          autoFocus
        />
      </div>
      <div className="onboarding-fields-row">
        <div className="onboarding-field">
          <label>Setor *</label>
          <select
            value={data.industry}
            onChange={(e) => onChange({ industry: e.target.value })}
          >
            <option value="">Selecione...</option>
            {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
        <div className="onboarding-field">
          <label>Tamanho do time *</label>
          <select
            value={data.teamSize}
            onChange={(e) => onChange({ teamSize: e.target.value })}
          >
            <option value="">Selecione...</option>
            {TEAM_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

// ─── Step 4: Team Invite ─────────────────────────────────────

function StepTeamInvite({
  data,
  onChange,
}: {
  data: OnboardingData;
  onChange: (fields: Partial<OnboardingData>) => void;
}) {
  const [currentEmail, setCurrentEmail] = useState('');

  const addEmail = () => {
    const email = currentEmail.trim().toLowerCase();
    if (email && email.includes('@') && !data.inviteEmails.includes(email)) {
      onChange({ inviteEmails: [...data.inviteEmails, email] });
      setCurrentEmail('');
    }
  };

  const removeEmail = (email: string) => {
    onChange({ inviteEmails: data.inviteEmails.filter(e => e !== email) });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail();
    }
  };

  return (
    <div className="onboarding-step-body">
      <div className="onboarding-step-icon"><Mail size={28} /></div>
      <h2>Convide sua Equipe</h2>
      <p className="onboarding-step-desc">
        Adicione e-mails de colegas para convidá-los ao workspace.
        Você pode pular e convidar depois.
      </p>
      <div className="onboarding-field">
        <label>E-mail do colega</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            type="email"
            value={currentEmail}
            onChange={(e) => setCurrentEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="colega@empresa.com"
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="onboarding-btn-secondary"
            onClick={addEmail}
            disabled={!currentEmail.includes('@')}
            style={{ padding: '8px 12px' }}
          >
            <Plus size={16} />
          </button>
        </div>
      </div>
      {data.inviteEmails.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
          {data.inviteEmails.map((email) => (
            <div
              key={email}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '13px',
                background: 'var(--bg-surface, #f5f5f5)',
                border: '1px solid var(--border-subtle, #e5e5e5)',
              }}
            >
              <span>{email}</span>
              <button
                type="button"
                onClick={() => removeEmail(email)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-secondary, #888)' }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <span style={{ fontSize: '11px', color: 'var(--text-secondary, #888)' }}>
            {data.inviteEmails.length} convite(s) pendente(s)
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Step 5: Done ─────────────────────────────────────────────

function StepDone({ workspaceName, inviteCount }: { workspaceName: string; inviteCount: number }) {
  return (
    <div className="onboarding-step-body onboarding-done">
      <div className="onboarding-done-icon"><CheckCircle2 size={48} /></div>
      <h2>Workspace criado!</h2>
      <p className="onboarding-step-desc">
        <strong>{workspaceName}</strong> está pronto. Você tem 14 dias de trial gratuito.
        Explore todas as funcionalidades sem restrições.
      </p>
      <ul className="onboarding-done-checklist">
        <li><CheckCircle2 size={14} /> Workspace configurado</li>
        <li><CheckCircle2 size={14} /> Organização criada</li>
        <li><CheckCircle2 size={14} /> Trial de 14 dias ativo</li>
        <li><CheckCircle2 size={14} /> Você é o administrador</li>
        {inviteCount > 0 && (
          <li><Mail size={14} /> {inviteCount} convite(s) enviado(s)</li>
        )}
      </ul>
    </div>
  );
}
